import React from 'react'
import {useWriteStatusStore} from '@/protocols/persistence'
import {useUploadStatusStore} from '@/protocols/persistence/useUploadStatusStore'
import {Box, Button, Stack, Text, Tooltip} from '@mantine/core'
import {IconCloudUpload, IconFiles} from "@tabler/icons-react";
import cx from "clsx";
import classes from "./PendingWritesTray.module.css"
import commonClasses from "@/common/common.module.css"
import {useNeurotuneStore} from "@/common/store/neurotune.ts";

export const PendingWritesTray: React.FC = () => {
    const pendingCount = useWriteStatusStore(s => s.pendingCount)
    const deadCount = useWriteStatusStore(s => s.deadCount)
    const summary = useUploadStatusStore((s) => s.summary)
    const serverFileCount = useUploadStatusStore((s) => s.serverFileCount)
    const browserUploads = useUploadStatusStore((s) => s.browserUploads)

    // In-flight uploads are anything the watcher/upload worker has
    // detected but hasn't yet confirmed on the server. We count all
    // four transient states (found/copied/pending/uploading) so the
    // tray accurately reflects "stuff still moving." `persisted` is
    // intentionally excluded — those rows are done.
    const uploadInFlight =
        summary.found + summary.copied + summary.pending + summary.uploading
    const uploadDead = summary.dead
    // Browser-mode direct uploads don't pass through the Rust queue
    // (summary is zeros there), so count them separately: in-flight XHR
    // uploads plus failed uploads that are still retryable/dismissable.
    let browserActive = 0
    browserUploads.forEach((e) => {
        if (e.status === 'in_flight' || e.status === 'dead') browserActive += 1
    })
    const totalPending = pendingCount + uploadInFlight + browserActive
    const totalDead = deadCount + uploadDead;
    const isOnline = useNeurotuneStore((s) => s.isOnline)

    const statusClass = isOnline ? classes.online : classes.offline
    const statusLabel = isOnline ? "Online" : "Offline"

    return (
        <Box p={"0.25rem"} style={{justifySelf: "end", display: "flex", alignItems: "center", gap: "0.5em"}}>
            <Tooltip position="bottom-end" label={
                <Stack gap={4}>
                    <Box>
                        <Text>Local pending activity and server reachability.</Text>
                        <ul>
                            <li><Box component="span" mr={"0.5em"}><IconCloudUpload
                                className={cx(classes.upload, classes.pulsing)}/></Box>
                                Edits & Files pending replication.
                                <ul>
                                    <li>Pulses when actively syncing.</li>
                                    <li>Red failures require attention.</li>
                                </ul>
                            </li>
                            <Box h={"0.5em"}/>
                            <li><Box component="span" mr={"0.5em"}><IconFiles className={cx(classes.upload)}/></Box>
                                Files uploaded to the server.
                            </li>
                            <Box h={"0.5em"}/>
                            <li><Box m={0} p={0} className={cx(classes.statusDot, classes.online)}
                                     aria-hidden="true"/> Online / <span><Box m={0} p={0}
                                                                              className={cx(classes.statusDot, classes.offline)}
                                                                              aria-hidden="true"/> Offline — Server Status.</span>
                            </li>
                            <Box h={"0.5em"}/>
                            <li><b>Click to open the activity dialog.</b></li>
                        </ul>
                    </Box>
                </Stack>
            }>
                <Button
                    className={cx(commonClasses.dropshadowHover, classes.button)}
                    onClick={() => {
                        window.dispatchEvent(new CustomEvent('show-pending-activity-modal'))
                    }}
                    variant={"outline"}
                    data-server-status={isOnline ? "online" : "offline"}
                    data-testid="pending-writes-tray"
                    leftSection={
                        <Box w={"3em"} p={"0.12em"} style={{
                            display: "grid",
                            grid: "auto / auto auto ",
                            colGap: "0.25em",
                            alignItems: "center",
                        }}>
                            <IconCloudUpload
                                data-testid="icon-cloud-upload"
                                className={cx(
                                    classes.upload,
                                    totalDead > 0 && classes.dead,
                                    (pendingCount > 0 || uploadInFlight > 0) && classes.pulsing,
                                )}
                            />
                            <span data-testid="left-badge">{totalPending + totalDead}</span>
                        </Box>
                    }
                    rightSection={
                        <Box w={"3em"} p={"0.12em"} style={{
                            display: "grid",
                            grid: "auto / auto auto",
                            colGap: "0.25em",
                            alignItems: "center",
                        }}>
                            <IconFiles
                                data-testid="icon-files"
                                className={cx(classes.upload)}
                            />
                            <span data-testid="right-badge">{serverFileCount}</span>
                        </Box>
                    }
                >
                    <span
                        data-testid="server-status-dot"
                        className={cx(classes.statusDot, statusClass)}
                        aria-hidden="true"
                    />
                    {statusLabel}
                </Button>
            </Tooltip>
        </Box>
    )
}
